import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReactMic } from 'react-mic';
import { useSharedState } from '../hooks/SharedStateContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStop } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-simple-toasts';
import * as Sentry from '@sentry/react';
import useRecordingWatchdog from '../hooks/useRecordingWatchdog';
import usePressAndHoldRecording from '../hooks/usePressAndHoldRecording';
import useMicOpenGate from '../hooks/useMicOpenGate';
import { trackRecordingStuckRecovered } from '../utilities/tracking';
import { primeStreamingAudio, isTTSStreamingEnabled, releaseStuckStream } from '../utilities/ttsStream';
import { requestPlaybackStop } from '../utilities/playbackControl';

import './Recording.css'

const MAX_RECORDING_TIME = 50000; // 50 seconds in milliseconds
// Shorter than this, a recording is a double-click or a fumbled tap, not speech: sent
// anyway, the model answers the noise with something invented — a stray sentence, or
// a whole reply in the wrong language. Holds can't get here (the 400ms hold threshold
// plus the release tail already exceed it), and no real tap-speak-tap turn is this quick.
export const MIN_TURN_MS = 500;
const PREFERRED_MIME_TYPE = 'audio/mp3';
// Real-world fallbacks that browser MediaRecorders actually encode. No browser
// supports MP3 encoding, so in practice one of these is selected.
const FALLBACK_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

// react-mic stamps the recorded Blob's type directly from this value
// (new Blob(chunks, { type: mimeType })). Returning an empty/unsupported type
// produces a Blob whose `.type` fails the `audio/*` guard in ConversationWT,
// so we must always return a concrete, supported audio mime type.
export function getRecordingMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    // Can't probe support (SSR/tests): keep the historical default so the blob stays tagged audio/*.
    return PREFERRED_MIME_TYPE;
  }

  const supported = [PREFERRED_MIME_TYPE, ...FALLBACK_MIME_TYPES]
    .find(type => MediaRecorder.isTypeSupported(type));

  return supported || PREFERRED_MIME_TYPE;
}

// Check if real AudioContext is available (not our polyfill stub)
const isAudioSupported = typeof window !== 'undefined' &&
  window.AudioContext &&
  window.AudioContext.name !== 'AudioContextStub';

export default function Recording() {
// class Recording extends React.Component {

  // https://github.com/hackingbeauty/react-mic?tab=readme-ov-file

  const { sharedState, setSharedState } = useSharedState();
  const { t } = useTranslation();
  const [isMicVisible, setIsMicVisible] = useState(false); // Start with false if you want it hidden initially
  const [isHolding, setIsHolding] = useState(false);
  const recordingTimerRef = useRef(null);
  const recordingStartedAtRef = useRef(0);
  // Set when a recording ends with nothing worth sending (abandoned gesture, too short,
  // mic never opened): react-mic still hands us a blob, and this tells onStop to drop it.
  const discardNextBlobRef = useRef(false);
  const recordingMimeType = getRecordingMimeType();
  const micGate = useMicOpenGate(sharedState.record);

  /**
   * The recorded turn never resolved. Give the learner their microphone back rather than
   * leaving them on a dead screen, and make the event loud enough that we hear about it
   * from monitoring instead of from a one-star review.
   */
  const releaseStuckRecording = useCallback((stuckForMs) => {
    console.error(`Recording watchdog: microphone locked for ${stuckForMs}ms with no reply, releasing`);

    // A reply still streaming in must not play over the learner's next turn.
    const streamWasInFlight = releaseStuckStream();

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      record: false,
      lastRecordedBlob: null,
    }));

    toast(t('recording_timeout') || 'The connection timed out. Please try recording again.');

    const activityId = sharedState.selectedActivity;
    trackRecordingStuckRecovered(activityId, stuckForMs);
    Sentry.captureMessage('Recording watchdog released a stuck microphone', {
      level: 'error',
      tags: { function: 'useRecordingWatchdog', activityId },
      extra: { stuckForMs, streamWasInFlight },
    });
  }, [setSharedState, t, sharedState.selectedActivity]);

  const armRecordingWatchdog = useRecordingWatchdog({
    loading: sharedState.loading,
    onStuck: releaseStuckRecording,
  });

  const startRecording = () => {
    // Silence anything Madame AI is saying BEFORE the mic opens — otherwise the
    // recording picks up her own voice and the model transcribes it as the
    // learner's speech. One dispatch kills both playback paths: the <audio>
    // element (ConversationComponent's listener) and the streamed Web Audio
    // queue (ttsStream's module listener).
    requestPlaybackStop('recording-started');

    discardNextBlobRef.current = false;
    recordingStartedAtRef.current = Date.now();
    micGate.markOpening();

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      record: true,
    }));
    setIsMicVisible(true); // Show the ReactMic when starting

    // Start 50-second auto-stop timer
    recordingTimerRef.current = setTimeout(() => {
      stopRecording();
      toast(t('max_recording_reached'));
    }, MAX_RECORDING_TIME);
  }
 
  const stopRecording = () => {
    // Unlock the streaming-TTS AudioContext while we are still inside the user's
    // stop-recording gesture: iOS only honours resume() on the gesture's call stack,
    // and by the time the reply's audio chunks arrive there is no gesture left.
    // Must stay synchronous and first — anything async before it breaks the unlock.
    if (isTTSStreamingEnabled()) {
      primeStreamingAudio();
    }

    // Clear the auto-stop timer
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    const tooShort = Date.now() - recordingStartedAtRef.current < MIN_TURN_MS;
    const micWasOpen = micGate.markStopped();
    discardNextBlobRef.current = tooShort || !micWasOpen;
    if (tooShort) {
      toast(t('recording_too_short'));
    }

    setSharedState(prevState => ({
      ...prevState,
      record: false,
    }));
    setIsMicVisible(false); // Show the ReactMic when starting
  };

  /**
   * Close the mic without sending a turn. Used when a press-and-hold gesture is taken
   * away mid-press (the press turned into a scroll, the browser stole the pointer):
   * the learner never meant to record, so nothing should reach the tutor. Deliberately
   * not stopRecording — there is no user gesture to prime iOS audio with, and no turn
   * to keep the last syllable for.
   */
  const cancelRecording = () => {
    if (!sharedState.record) return;

    discardNextBlobRef.current = true;
    micGate.markStopped();

    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setSharedState(prevState => ({
      ...prevState,
      record: false,
    }));
    setIsMicVisible(false);
  };

  /**
   * Press-and-hold keeps the mic open for a beat after the button is released, which
   * puts the actual stop outside the gesture — and iOS only unlocks the streaming-TTS
   * AudioContext on the gesture's own call stack. Unlock it on the release itself;
   * stopRecording's own call then finds it already running.
   */
  const primeAudioForRelease = () => {
    if (isTTSStreamingEnabled()) {
      primeStreamingAudio();
    }
  };

  const recordingGestureHandlers = usePressAndHoldRecording({
    isRecording: sharedState.record,
    disabled: sharedState.loading,
    onStart: startRecording,
    onStop: stopRecording,
    onCancel: cancelRecording,
    onReleaseCommit: primeAudioForRelease,
    onHoldingChange: setIsHolding,
  });

  const onStop = (recordedBlob) => {
    // console.log('recordedBlob is: ', recordedBlob);

    // Nothing in this recording was meant for the tutor (see stopRecording and
    // cancelRecording): react-mic hands the blob over anyway, and sending it would put an
    // accidental turn — a fraction of a second of room noise — in front of her.
    if (discardNextBlobRef.current) {
      discardNextBlobRef.current = false;
      return;
    }

    // `loading` disables the mic button until the turn resolves. Arm the watchdog in the
    // same breath so the latch can never outlive the turn that set it (MAD-788).
    armRecordingWatchdog();

    setSharedState(prevState => ({
      ...prevState,
      loading: true,
      lastRecordedBlob: recordedBlob,
    }));
  }

  // Cleanup timer on unmount or when recording stops
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }
    };
  }, []);

  // Show message if audio recording isn't supported
  if (!isAudioSupported) {
    return (
      <div className="recording-controls">
        {sharedState.firstUserInput && !sharedState.showActivities &&
          <p className="audio-unsupported-message">
            {t('audio_not_supported', 'Voice recording is not supported on this browser. Please use a different browser or device.')}
          </p>
        }
      </div>
    );
  }

  return (
    <div className="recording-controls">
      {sharedState.firstUserInput && !sharedState.showActivities &&
        <div className="recording-panel">

          <div className="recording-button-row">
            <button
              disabled={sharedState.loading}
              {...recordingGestureHandlers}
              className={`recording-control${sharedState.record ? ' recording' : ''}${isHolding ? ' holding' : ''}`}
              type="button"
              aria-pressed={sharedState.record}
              aria-label={sharedState.record ? t('recording_stop_label') : t('recording_start_label')}
              aria-describedby="recording-mode-hint">
              <FontAwesomeIcon icon={sharedState.record ? faStop : faMicrophone} />
            </button>
          </div>

          <ReactMic
          record={micGate.micRecord}
          visualSetting="sinewave"
          className={`sound-wave ${isMicVisible ? '' : 'hidden'}`}
          onStop={onStop}
          onData={micGate.handleMicData}
          mimeType={recordingMimeType} // MP3 when supported, otherwise the first audio/* format the browser can actually record.
          strokeColor="#181632"
          backgroundColor="#eef1f6"
          /* Match the onboarding mic-test waveform: AGC boosts quiet speech so the
             waveform moves more (noise/echo already off by react-mic default). */
          autoGainControl={true}
          noiseSuppression={false}
          echoCancellation={false} />

          {/* Both interactions are on the same button, so the button has to say so —
              otherwise press-and-hold is a feature nobody discovers. The hint follows
              the gesture the learner is actually in the middle of. */}
          <p className="recording-mode-hint" id="recording-mode-hint">
            {isHolding
              ? t('recording_hint_release')
              : sharedState.record
                ? t('recording_hint_stop')
                : t('recording_hint_idle')}
          </p>
        </div>
      }
    </div>
  );
}
